import request from './request'

export type DefaultChecklistSeasonKey = string

export interface DefaultChecklistMetaOption {
  value: string
  label: string
}

export interface DefaultChecklistApplyTemplate {
  id: number
  value: number
  label: string
  season_key: string
}

export interface DefaultChecklistPeriodLayout {
  period_type: string
  total_slots: number
  display_layout: {
    type: string
    columns: number
    rows: number
    direction: string
  }
}

export interface DefaultChecklistMetaData {
  seasons: DefaultChecklistMetaOption[]
  item_types: DefaultChecklistMetaOption[]
  period_layout: DefaultChecklistPeriodLayout
  apply_templates: DefaultChecklistApplyTemplate[]
}

export interface DefaultChecklistMetaResponse {
  code: number
  msg: string
  data?: DefaultChecklistMetaData
}

/** POST `/api/admin/default-checklist/meta` */
export function getDefaultChecklistMeta(): Promise<DefaultChecklistMetaResponse> {
  return request.post('/api/admin/default-checklist/meta', {})
}

export interface DefaultChecklistListItem {
  id: number
  item_id: string
  item_name: string
  item_name_zh?: string
  type?: string
  type_text?: string
  section?: string
  duration_minutes?: number
  duration_text?: string
  frequency?: string
  frequency_text?: string
  status?: number
}

export interface DefaultChecklistTemplate {
  id: number
  season_key: string
  season_name: string
  template_name: string
  items: DefaultChecklistListItem[]
  baseline_items?: DefaultChecklistListItem[]
  routine_items?: DefaultChecklistListItem[]
  item_count: number
  status: number
  status_text: string
  sort: number
  create_time: string
  update_time: string
}

export interface DefaultChecklistListData {
  period_layout?: DefaultChecklistPeriodLayout
  list: DefaultChecklistTemplate[]
  options?: DefaultChecklistApplyTemplate[]
}

export interface DefaultChecklistListParams {
  exclude_template_id?: number
}

export interface DefaultChecklistListResponse {
  code: number
  msg: string
  data?: DefaultChecklistListData
}

/** POST `/api/admin/default-checklist/list` */
export function getDefaultChecklistList(
  params: DefaultChecklistListParams = {},
): Promise<DefaultChecklistListResponse> {
  return request.post('/api/admin/default-checklist/list', params)
}

export async function loadDefaultChecklistPage(): Promise<{
  meta: DefaultChecklistMetaResponse
  list: DefaultChecklistListResponse
}> {
  const [meta, list] = await Promise.all([
    request.post('/api/admin/default-checklist/meta', {}) as Promise<DefaultChecklistMetaResponse>,
    request.post('/api/admin/default-checklist/list', {}) as Promise<DefaultChecklistListResponse>,
  ])
  return { meta, list }
}

export interface DefaultChecklistUpdateItemsParams {
  template_id: number
  baseline_checklist_ids: number[]
  routine_checklist_ids: number[]
}

export interface DefaultChecklistUpdateItemsResponse {
  code: number
  msg: string
  data?: DefaultChecklistTemplate
}

/** POST `/api/admin/default-checklist/update-items` */
export function updateDefaultChecklistItems(
  params: DefaultChecklistUpdateItemsParams,
): Promise<DefaultChecklistUpdateItemsResponse> {
  return request.post('/api/admin/default-checklist/update-items', params)
}

export interface DefaultChecklistApplyParams {
  source_id: number
  target_id: number
  source_template_id: number
  target_template_id: number
  confirm_replace: number
}

export interface DefaultChecklistApplyData {
  source?: DefaultChecklistTemplate
  target?: DefaultChecklistTemplate
  need_confirm_replace?: number
  target_template_id?: number
  target_item_count?: number
}

export interface DefaultChecklistApplyResponse {
  code: number
  msg: string
  data?: DefaultChecklistApplyData
}

export interface DefaultChecklistDetailParams {
  id: number
  template_id?: number
}

export interface DefaultChecklistDetailData extends DefaultChecklistTemplate {
  baseline_checklist_ids?: number[]
  routine_checklist_ids?: number[]
}

export interface DefaultChecklistDetailResponse {
  code: number
  msg: string
  data?: DefaultChecklistDetailData
}

/** POST `/api/admin/default-checklist/detail` */
export function getDefaultChecklistDetail(
  params: DefaultChecklistDetailParams,
): Promise<DefaultChecklistDetailResponse> {
  return request.post('/api/admin/default-checklist/detail', params)
}

/** POST `/api/admin/default-checklist/apply` */
export function applyDefaultChecklist(
  params: DefaultChecklistApplyParams,
): Promise<DefaultChecklistApplyResponse> {
  return request.post('/api/admin/default-checklist/apply', params)
}
